import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Drawer,
  Stack, Button, MenuItem, CircularProgress, Chip, Alert,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  getUsersApi, editUserApi, deleteUserApi,
  resetPasswordApi, resetSubscriptionApi, getUsersAnalyticsApi,
} from '../../services/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';

const fmtDate = (v) => {
  if (!v) return 'N/A';
  const d = new Date(v);
  if (isNaN(d)) return 'N/A';
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
};

const parseSubType = (receiptId) => {
  try {
    const r = JSON.parse(JSON.parse(receiptId || '{}'));
    if (r.productId === 'jamsap_green_sub') return { label: 'Green', color: 'success' };
    if (r.productId === 'jamsap_gold_sub') return { label: 'Gold', color: 'warning' };
    return { label: 'Unknown', color: 'default' };
  } catch { return null; }
};

const COLUMNS = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
  { field: 'phoneNumber', headerName: 'Phone', flex: 1, minWidth: 130 },
  { field: 'device', headerName: 'Device', width: 100 },
  {
    field: 'receiptId', headerName: 'Subscription', width: 150, sortable: false,
    renderCell: (p) => {
      const sub = parseSubType(p.value);
      if (!sub) return <Chip label="Free" size="small" />;
      return <Chip label={sub.label} size="small" color={sub.color} />;
    },
  },
  { field: 'referral', headerName: 'Referral', width: 120 },
  {
    field: 'createdAt', headerName: 'Joined', width: 170,
    renderCell: (p) => fmtDate(p.value),
  },
  {
    field: 'purchaseTime', headerName: 'Subscribed At', width: 170,
    sortingOrder: ['desc', 'asc', null],
    renderCell: (p) => fmtDate(p.row?.subscribeInfo?.purchaseTime),
  },
];

export default function Users() {
  const { user: authUser } = useAuth();
  const canWrite = !authUser?.adminAccess?.users || authUser.adminAccess.users.write === true;

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortModel, setSortModel] = useState([{ field: 'createdAt', sort: 'desc' }]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerForm, setDrawerForm] = useState({});
  const [initialDrawerForm, setInitialDrawerForm] = useState({});
  const [drawerError, setDrawerError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const drawerIsDirty = JSON.stringify(drawerForm) !== JSON.stringify(initialDrawerForm);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // Map DataGrid field names to actual API sort fields
      const SORT_FIELD_MAP = { purchaseTime: 'subscribeInfo.purchaseTime' };
      const sortParam = sortModel[0]
        ? `${SORT_FIELD_MAP[sortModel[0].field] ?? sortModel[0].field}:${sortModel[0].sort}`
        : undefined;
      const { data } = await getUsersApi({ page: page + 1, size: pageSize, sortBy: sortParam, search });
      setUsers(data.rows.map((u) => ({ ...u, id: u._id })));
      setTotal(data.total);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [page, pageSize, sortModel, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    getUsersAnalyticsApi().then(({ data }) => setAnalytics(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedUser) {
      setDrawerForm(selectedUser);
      setInitialDrawerForm(selectedUser);
      setShowAdvanced(false);
      setDrawerError('');
    }
  }, [selectedUser]);

  const handleExport = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Users');
    ws.columns = [
      { header: 'ID', key: '_id', width: 25 },
      { header: 'Name', key: 'name', width: 25 },
      { header: 'Email', key: 'email', width: 30 },
      { header: 'Phone', key: 'phoneNumber', width: 18 },
      { header: 'Device', key: 'device', width: 15 },
      { header: 'Subscription', key: 'sub', width: 20 },
      { header: 'Referral', key: 'referral', width: 18 },
      { header: 'Joined', key: 'createdAt', width: 22 },
      { header: 'Subscribed At', key: 'subscribedAt', width: 22 },
    ];
    users.forEach((u) => {
      const sub = parseSubType(u.receiptId);
      ws.addRow({ ...u, sub: sub ? sub.label : 'Free', createdAt: fmtDate(u.createdAt), subscribedAt: fmtDate(u.subscribeInfo?.purchaseTime) });
    });
    ws.getRow(1).font = { bold: true };
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), 'JAMSAP_Users.xlsx');
  };

  const handleDrawerSave = async () => {
    setDrawerLoading(true); setDrawerError('');
    try {
      await editUserApi(drawerForm._id, drawerForm);
      setInitialDrawerForm(drawerForm);
      fetchUsers();
      setSelectedUser(null);
    } catch (e) { setDrawerError(e?.response?.data?.error || 'Save failed'); }
    finally { setDrawerLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this user?')) return;
    setDrawerLoading(true);
    try { await deleteUserApi(drawerForm._id); fetchUsers(); setSelectedUser(null); }
    catch (e) { setDrawerError(e?.response?.data?.error || 'Delete failed'); setDrawerLoading(false); }
  };

  const handleResetPassword = async () => {
    await resetPasswordApi(drawerForm.email);
    alert('Password reset email sent.');
  };

  const handleResetSub = async () => {
    setDrawerLoading(true);
    try { await resetSubscriptionApi(drawerForm._id); fetchUsers(); setSelectedUser(null); }
    catch (e) { setDrawerError(e?.message || 'Failed'); setDrawerLoading(false); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Scrollable top section */}
      <Box sx={{ p: 3, pb: 0 }}>
        <PageHeader title="Users" subtitle="Manage JAMSAP TV subscribers" />

        {analytics && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <StatCard icon={<PeopleAltIcon />} label="Total Users" value={analytics.total ?? '—'} color="#6366f1" />
            <StatCard icon={<VerifiedIcon />} label="Green Subs" value={analytics.greenSubs ?? '—'} color="#22c55e" />
            <StatCard icon={<StarIcon />} label="Gold Subs" value={analytics.goldSubs ?? '—'} color="#f59e0b" />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search users…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              },
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            disabled={users.length === 0}
            size="small"
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* DataGrid fills remaining height */}
      <Box sx={{ flex: 1, minHeight: 0, px: 3, pb: 3 }}>
        <DataGrid
          rows={users}
          columns={COLUMNS}
          pagination
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page: p, pageSize: ps }) => { setPage(p); setPageSize(ps); }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => { setPage(0); setSortModel(model); }}
          loading={loading}
          onRowClick={(p) => setSelectedUser(p.row)}
          sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e8eaf0', cursor: 'pointer', height: '100%' }}
        />
      </Box>

      {/* User Detail Drawer */}
      <Drawer anchor="right" open={!!selectedUser} onClose={() => setSelectedUser(null)}
        PaperProps={{ sx: { width: 400 } }}>

        {/* Scrollable body */}
        <Box sx={{ p: 3, pb: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">User Details</Typography>
            {!canWrite && <Chip label="Read only" size="small" variant="outlined" color="default" />}
          </Stack>

          {!drawerForm._id ? <CircularProgress /> : (
            <Stack spacing={2}>
              <TextField
                label="User Name" name="userName"
                value={drawerForm.userName || ''}
                onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                fullWidth InputProps={{ readOnly: !canWrite }}
              />
              <TextField
                label="Name" name="name"
                value={drawerForm.name || ''}
                onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                fullWidth InputProps={{ readOnly: !canWrite }}
              />
              <TextField
                label="Email" name="email"
                value={drawerForm.email || ''}
                onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                fullWidth InputProps={{ readOnly: !canWrite }}
              />
              <TextField
                label="Phone Number" name="phoneNumber"
                value={drawerForm.phoneNumber || ''}
                onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                fullWidth InputProps={{ readOnly: !canWrite }}
              />
              <TextField
                label="Referral" name="referral"
                value={drawerForm.referral || ''}
                onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                fullWidth InputProps={{ readOnly: !canWrite }}
              />
              <TextField
                label="Age" name="age" type="number"
                value={drawerForm.age || ''}
                onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                fullWidth InputProps={{ readOnly: !canWrite }}
              />
              <TextField
                select label="Gender" name="gender"
                value={drawerForm.gender || ''}
                onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                fullWidth inputProps={{ readOnly: !canWrite }}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="N/A">N/A</MenuItem>
              </TextField>
              <TextField
                label="Address" name="address"
                value={drawerForm.address || ''}
                onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                fullWidth multiline InputProps={{ readOnly: !canWrite }}
              />

              {canWrite && (
                <>
                  {/* Show/Hide Advanced Fields */}
                  <Box mt={1}>
                    <Button fullWidth onClick={() => setShowAdvanced((v) => !v)} variant="text">
                      {showAdvanced ? 'Hide Advanced Fields' : 'Show Advanced Fields'}
                    </Button>
                  </Box>

                  {showAdvanced && (
                    <>
                      <TextField
                        select label="User Type" name="userType"
                        value={drawerForm.userType || ''}
                        onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                        fullWidth
                      >
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="superadmin">Super Admin</MenuItem>
                      </TextField>
                      <TextField
                        label="Social Login ID" name="socialLoginId"
                        value={drawerForm.socialLoginId || ''}
                        onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                        fullWidth
                      />
                      <TextField
                        label="Social Platform" name="socialPlatform"
                        value={drawerForm.socialPlatform || ''}
                        onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                        fullWidth
                      />
                      <TextField
                        label="Billing Info" name="billingInfo"
                        value={drawerForm.billingInfo || ''}
                        onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                        fullWidth multiline
                      />
                      <TextField
                        label="Receipt ID" name="receiptId"
                        value={drawerForm.receiptId || ''}
                        onChange={(e) => setDrawerForm((f) => ({ ...f, [e.target.name]: e.target.value }))}
                        fullWidth
                      />
                      {drawerForm.receiptId && drawerForm.receiptId !== '' && (
                        <Button
                          variant="outlined" color="secondary" fullWidth
                          onClick={handleResetSub} disabled={drawerLoading}
                          sx={{ mt: '10px !important', mb: '20px !important' }}
                        >
                          {drawerLoading ? <CircularProgress size={20} /> : 'Reset Subscription Info'}
                        </Button>
                      )}
                    </>
                  )}
                </>
              )}

              <Box mt={3}>
                <Stack spacing={2}>
                  <Button variant="outlined" color="secondary" fullWidth onClick={handleResetPassword}>
                    Reset Password
                  </Button>
                  {canWrite && (
                    <Button variant="text" fullWidth onClick={handleDelete}>
                      Delete User
                    </Button>
                  )}
                </Stack>
              </Box>
            </Stack>
          )}
        </Box>

        {/* Sticky Save button at bottom */}
        {canWrite && (
          <Box sx={{ borderTop: '1px solid #eee', p: 2, position: 'sticky', bottom: 0, bgcolor: '#fff', zIndex: 1 }}>
            {drawerError && <Alert severity="error" sx={{ mb: 1 }}>{drawerError}</Alert>}
            <Button
              variant="contained" color="primary" fullWidth
              onClick={handleDrawerSave}
              disabled={!drawerIsDirty || drawerLoading}
            >
              {drawerLoading ? 'Updating...' : 'Update Profile'}
            </Button>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}